import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import DataTable from "react-data-table-component";
import { userRequest } from "../utils/requestMethods";
import { formatter, getPriceForTerm } from "../utils/currencyUtils";
import {
  FiMoreHorizontal,
  FiUsers,
  FiLayers,
  FiPlus,
  FiLink,
  FiGrid,
  FiList,
  FiTrash2,
  FiArrowRight,
  FiCheckSquare,
  FiAward,
} from "react-icons/fi";
import {
  Header,
  AddClassModal,
  CustomPagination,
  TableDropdown,
  SkeletonTable,
  StudentPromotionModal,
  ClassTransferModal,
} from "../components";
import ClassDetailModal from "../components/ClassDetailModal";
import PromotionStatusBadge from "../components/PromotionStatusBadge";
import { useStateContext } from "../contexts/ContextProvider";
import useKYCStatusRedirect from "../hooks/useKYCStatusRedirect";
import * as Icons from "../data/class";
import { MdArrowCircleUp, MdEdit, MdVisibility, MdSwapVert } from "react-icons/md";
import { toast } from "react-toastify";
import * as classGroupApi from "../utils/classGroupApi";
import useClassGroups from "../hooks/useClassGroups";
import CreateClassGroupModal from "../components/classGroups/CreateClassGroupModal";
import LinkSubclassesModal from "../components/classGroups/LinkSubclassesModal";
import { ConfirmationModal } from "../components";
import { detectArmGroups, pluralize } from "../utils/classGroupHelpers";

const GROUPING_PROMPT_DISMISSED_KEY = "classGroupingPromptDismissed";

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "rgb(248, 250, 252)",
      fontWeight: "bold",
    },
  },
};

const Classes = ({ history }) => {
  const { school, refreshSchool } = useStateContext();
  const schoolTerm = school?.settings?.current_term;
  const schoolID = school?._id;
  const schoolCode = school?.code;

  // Class grouping hook
  const {
    classGroups,
    loading: loadingGroups,
    refreshClassGroups,
    isEnabled: isGroupingEnabled,
    subclassLabel,
  } = useClassGroups();
  const armsPlural = pluralize(subclassLabel, 2).toLowerCase();

  const [classes, setClasses] = useState([]);
  const [viewMode, setViewMode] = useState("all"); // 'all' | 'groups'

  // Class creation state
  const [class_name, setClassname] = useState("");
  const [price1, setPrice1] = useState("");
  const [price2, setPrice2] = useState("");
  const [price3, setPrice3] = useState("");
  const [level_of_education, setLevelOfEducation] = useState("");
  const [next_class, setNextClass] = useState("");
  const [is_last_class, setIsLastClass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [selectedClassForTransfer, setSelectedClassForTransfer] = useState(null);
  const [isClassDetailModalOpen, setIsClassDetailModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedClassForDetail, setSelectedClassForDetail] = useState(null);
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  // Grouping modals state
  const [addClassMode, setAddClassMode] = useState("single"); // 'single' | 'grouped'
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [classesToLink, setClassesToLink] = useState([]);
  const [targetGroupIdForLink, setTargetGroupIdForLink] = useState("");
  // Set when the link modal opens straight after creating a group, which isn't in
  // classGroups yet — lets the modal show the group's name and lock it as the target.
  const [justCreatedGroup, setJustCreatedGroup] = useState(null);
  const [enablingGrouping, setEnablingGrouping] = useState(false);
  // Pending "go to the new group" redirect after the create-class-level wizard; cancelled
  // on unmount so it can't pull the admin back if they have already navigated away.
  const groupRedirectTimer = useRef(null);
  useEffect(() => () => clearTimeout(groupRedirectTimer.current), []);
  const [groupingPromptDismissed, setGroupingPromptDismissed] = useState(() => {
    try {
      return localStorage.getItem(GROUPING_PROMPT_DISMISSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [unlinkingClass, setUnlinkingClass] = useState(null);
  const [unlinkingLoading, setUnlinkingLoading] = useState(false);
  const [deletingClass, setDeletingClass] = useState(null);
  const [deletingClassLoading, setDeletingClassLoading] = useState(false);

  // Stable reference so LinkSubclassesModal's selection state doesn't get reset by
  // an unrelated parent re-render recomputing a fresh array on every render.
  const linkModalAvailableClasses = useMemo(() => {
    return targetGroupIdForLink
      ? classes?.filter(
          (c) => classGroupApi.getClassGroupId(c.class_group_id) !== targetGroupIdForLink,
        )
      : classes || [];
  }, [classes, targetGroupIdForLink]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const openAddClass = (mode = "single") => {
    setAddClassMode(mode);
    setIsModalOpen(true);
  };

  // Ungrouped classes whose names look like arms of one level (JSS 1A, JSS 1B…).
  const detectedArmGroups = useMemo(() => detectArmGroups(classes || []), [classes]);

  const dismissGroupingPrompt = () => {
    setGroupingPromptDismissed(true);
    try {
      localStorage.setItem(GROUPING_PROMPT_DISMISSED_KEY, "true");
    } catch {
      // Storage unavailable — the prompt just reappears next visit.
    }
  };

  const handleEnableGrouping = async () => {
    try {
      setEnablingGrouping(true);
      await userRequest.post(`/schools/${schoolID}/settings`, { class_grouping_enabled: true });
      await refreshSchool();
      setViewMode("groups");
      toast.success("Class grouping is on — create your first class group below");
    } catch (err) {
      console.error("Error enabling class grouping:", err);
      toast.error("Couldn't turn on class grouping. You can also do it from Settings.");
    } finally {
      setEnablingGrouping(false);
    }
  };

  const handleGroupCreated = (createdGroup, classesToPreselect = []) => {
    setJustCreatedGroup(createdGroup || null);
    setTargetGroupIdForLink(createdGroup?._id || "");
    setClassesToLink(classesToPreselect);
    setIsLinkModalOpen(true);
    refreshClassGroups();
    fetchClasses();
    setViewMode("groups");
  };

  const togglePromotionModal = async (classData = null) => {
    setSelectedClass(classData);
    if (classData && !isPromotionModalOpen) {
      await fetchStudentsForClass(classData._id);
      if (classData.next_class) {
        const nextClassData = await fetchNextClassData(classData.next_class);
        setSelectedClass((prev) => ({
          ...prev,
          nextClassData,
        }));
      }
    }
    setIsPromotionModalOpen(!isPromotionModalOpen);
  };

  const toggleTransferModal = async (classData = null) => {
    setSelectedClassForTransfer(classData);
    if (classData && !isTransferModalOpen) {
      await fetchStudentsForClass(classData._id);
    }
    setIsTransferModalOpen(!isTransferModalOpen);
  };

  const toggleClassDetailModal = (classData = null) => {
    setSelectedClassForDetail(classData);
    setIsClassDetailModalOpen(!isClassDetailModalOpen);
  };

  const handlePromotionSuccess = () => {
    fetchClasses();
  };

  const [students, setStudents] = useState([]);

  const fetchStudentsForClass = async (classId) => {
    try {
      const { data } = await userRequest.get(`children/class/${classId}?limit=200`);
      setStudents(data.children || []);
    } catch (err) {
      console.log("Error fetching students:", err);
      setStudents([]);
    }
  };

  const fetchNextClassData = async (nextClassId) => {
    if (!nextClassId) return null;
    try {
      const existingClass = classes?.find((cls) => cls._id === nextClassId);
      if (existingClass) return existingClass;
      const { data } = await userRequest.get(`classes/${schoolID}/${nextClassId}`);
      return data;
    } catch (err) {
      console.log("Error fetching next class:", err);
      return {
        _id: nextClassId,
        class_name: `Class ${nextClassId.slice(-4)}`,
        level_of_education: "primary",
      };
    }
  };

  const fetchClasses = useCallback(async () => {
    if (schoolCode) {
      try {
        setLoading(true);
        const { data } = await userRequest.get(`classes?code=${schoolCode}`);
        setClasses(data.classes || []);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    }
  }, [schoolCode]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  const submitHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!class_name || !price1 || !price2 || !price3 || !level_of_education) {
      setError("Please Fill all the Fields");
      setTimeout(() => {
        setError("");
      }, 5000);
      setLoading(false);
      return;
    }

    if (next_class && next_class.trim() !== "" && is_last_class) {
      setError("A class with a next class cannot be marked as the last class");
      setTimeout(() => {
        setError("");
      }, 5000);
      setLoading(false);
      return;
    }

    try {
      const requestBody = {
        schoolId: schoolID,
        class_name,
        class_price: {
          1: price1,
          2: price2,
          3: price3,
        },
        level_of_education,
        is_last_class,
      };

      if (next_class && next_class.trim() !== "") {
        requestBody.next_class = next_class;
      }

      await userRequest.post("/classes", requestBody);
      fetchClasses();
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setClassname("");
        setPrice1("");
        setPrice2("");
        setPrice3("");
        setLevelOfEducation("");
        setNextClass("");
        setIsLastClass(false);
        toggleModal();
      }, 2000);
    } catch (err) {
      console.log(err);
      setError(err.response?.data || "Failed to add class");
      setTimeout(() => {
        setError("");
      }, 5000);
      setLoading(false);
    }
    setLoading(false);
  };

  const handleDropdownClick = (rowIndex, event) => {
    const rect = event.target.getBoundingClientRect();
    setDropdownIndex(dropdownIndex === rowIndex ? null : rowIndex);
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleCloseDropdown = () => setDropdownIndex(null);

  // Unlink single subclass from group
  const handleConfirmUnlink = async () => {
    if (!unlinkingClass) return;
    try {
      setUnlinkingLoading(true);
      await classGroupApi.unlinkSubclassFromClassGroup(schoolID, unlinkingClass._id);
      toast.success(`${unlinkingClass.class_name} removed from its class group`);
      setUnlinkingClass(null);
      fetchClasses();
      refreshClassGroups();
    } catch (err) {
      console.error("Error unlinking subclass:", err);
      toast.error(classGroupApi.extractApiErrorMessage(err, "Failed to remove the class from its group"));
    } finally {
      setUnlinkingLoading(false);
    }
  };

  // Delete class — the backend cascades this to the students enrolled in it,
  // so the confirmation modal must say so explicitly before an admin confirms.
  const handleConfirmDeleteClass = async () => {
    if (!deletingClass) return;
    try {
      setDeletingClassLoading(true);
      await userRequest.delete(`/classes/${schoolID}/${deletingClass._id}`);
      toast.success(`${deletingClass.class_name} deleted successfully`);
      setDeletingClass(null);
      fetchClasses();
      refreshClassGroups();
    } catch (err) {
      console.error("Error deleting class:", err);
      toast.error(err.response?.data?.message || err.response?.data || "Failed to delete class");
    } finally {
      setDeletingClassLoading(false);
    }
  };

  // Group batch linking
  const handleBatchGroupSelected = () => {
    if (selectedRows.length === 0) return;
    setClassesToLink(selectedRows);
    setJustCreatedGroup(null);
    setTargetGroupIdForLink("");
    setIsLinkModalOpen(true);
  };

  const TeachersCell = ({ row }) => {
    const teachers = row.teachers || [];
    const teacherCount = teachers.length;

    if (teacherCount === 0) {
      return (
        <div className="flex items-center gap-2">
          <FiUsers className="text-gray-400 text-sm" />
          <span className="text-sm text-gray-500 font-semibold">0</span>
        </div>
      );
    }

    const primaryCount = teachers.filter((t) => t.role === "primary").length;
    const assistantCount = teachers.filter((t) => t.role === "assistant").length;

    return (
      <div className="flex items-center gap-2">
        <FiUsers className="text-indigo-600 text-sm" />
        <span className="text-sm font-semibold text-gray-700">{teacherCount}</span>
        {(primaryCount > 0 || assistantCount > 0) && (
          <div className="flex items-center gap-1 ml-1">
            {primaryCount > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                {primaryCount}P
              </span>
            )}
            {assistantCount > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                {assistantCount}A
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  // Build DataTable columns
  const columns = [
    {
      name: "Class Name",
      selector: (row) => {
        const group = classGroups.find(
          (g) => g._id === classGroupApi.getClassGroupId(row.class_group_id),
        );

        return (
          <div className="py-1">
            <span className="font-semibold text-slate-900 block">{row.class_name}</span>
            {isGroupingEnabled && (
              <div className="mt-0.5 flex items-center gap-1.5">
                {row.class_group_id ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                    <FiLayers className="w-3 h-3" />
                    {group?.name || "Class group"} • {subclassLabel} {row.group_label || "—"}
                  </span>
                ) : (
                  <span className="text-[11px] text-slate-400">Not in a class group</span>
                )}
              </div>
            )}
          </div>
        );
      },
      grow: 1.5,
    },
    {
      name: "Subjects",
      selector: (row) => (
        <span className="font-semibold">{row.subjectCount || row.subjects?.length || 0}</span>
      ),
    },
    {
      name: "Class Fee",
      selector: (row) => (
        <span className="font-semibold">
          {formatter.format(getPriceForTerm(row.class_price, schoolTerm))}
        </span>
      ),
    },
    {
      name: "Extra Fee",
      selector: (row) => <span className="font-semibold">{row.add_on?.length || 0}</span>,
    },
    {
      name: "Students",
      selector: (row) => <span className="font-semibold">{row.studentCount || 0}</span>,
    },
    {
      name: "Teachers",
      selector: (row) => <TeachersCell row={row} />,
      width: "150px",
    },
    {
      name: "Promotion Status",
      selector: (row) => {
        const nextClass = classes?.find((cls) => cls._id === row.next_class);
        return (
          <PromotionStatusBadge
            status={row}
            nextClass={nextClass}
            studentCount={row.studentCount || 0}
          />
        );
      },
      width: "200px",
    },
    {
      cell: (row, rowIndex) => (
        <div className="relative">
          <button
            className="p-2 text-gray-600 hover:text-gray-900"
            onClick={(e) => handleDropdownClick(rowIndex, e)}
          >
            <FiMoreHorizontal className="text-2xl" />
          </button>
          <TableDropdown
            isVisible={dropdownIndex === rowIndex}
            position={dropdownPosition}
            options={[
              {
                label: "View Students",
                icon: <img src={Icons.checkList} alt="Payout Icon" />,
                onClick: () => history.push(`/students/${row._id}`),
              },
              {
                label: "View Fees",
                icon: <img src={Icons.coupon04} alt="Payout Icon" />,
                onClick: () => history.push(`/addons/${row._id}/${row.class_name}`),
              },
              {
                label: "View Subjects",
                icon: <img src={Icons.notebook} alt="Payout Icon" />,
                onClick: () => history.push(`/subjects/${row._id}`),
              },
              {
                label: "Class Teachers",
                icon: <MdVisibility className="text-2xl text-slate-400" />,
                onClick: () => {
                  handleCloseDropdown();
                  toggleClassDetailModal(row);
                },
              },
              {
                label: "Edit Class Details",
                icon: <MdEdit className="text-2xl text-slate-400" />,
                onClick: () => history.push(`/update-class/${row._id}`),
              },
              ...(isGroupingEnabled
                ? [
                    {
                      label: row.class_group_id ? "Move to another group" : "Add to a class group",
                      icon: <FiLink className="text-lg text-indigo-500" />,
                      onClick: () => {
                        handleCloseDropdown();
                        setClassesToLink([row]);
                        // Let the admin choose the destination: targeting the class's own
                        // group would filter the class itself out of the modal's list.
                        setTargetGroupIdForLink("");
                        setJustCreatedGroup(null);
                        setIsLinkModalOpen(true);
                      },
                    },
                    ...(row.class_group_id
                      ? [
                          {
                            label: "Remove from group",
                            icon: <FiTrash2 className="text-lg text-rose-500" />,
                            onClick: () => {
                              handleCloseDropdown();
                              setUnlinkingClass(row);
                            },
                          },
                        ]
                      : []),
                  ]
                : []),
              {
                label: row.is_last_class ? "Graduate Students" : "Promote Students",
                icon: <MdArrowCircleUp className="text-2xl text-slate-400" />,
                onClick: () => togglePromotionModal(row),
                condition:
                  (row.is_last_class && row.studentCount > 0) ||
                  (!row.is_last_class && row.next_class && row.studentCount > 0),
              },
              {
                label: "Skip Promote / Demote",
                icon: <MdSwapVert className="text-2xl text-slate-400" />,
                onClick: () => toggleTransferModal(row),
                condition: row.studentCount > 0 && (classes?.length || 0) > 1,
              },
              {
                label: "Delete Class",
                icon: <FiTrash2 className="text-lg text-red-600" />,
                danger: true,
                onClick: () => {
                  handleCloseDropdown();
                  setDeletingClass(row);
                },
              },
            ]}
            onClose={handleCloseDropdown}
          />
        </div>
      ),
      width: "120px",
    },
  ];

  const totalSubjects = classes?.reduce(
    (acc, currentClass) => acc + (currentClass?.subjectCount || 0),
    0,
  );
  const totalStudents = classes?.reduce(
    (acc, currentClass) => acc + (currentClass?.studentCount || 0),
    0,
  );

  return (
    <>
      {/* Header */}
      <div className="px-4 md:px-10 pt-4 md:pt-10 mt-20 md:mt-0">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Header category="Set Up" title="Classes" />
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => history.push("/archived-students")}
              className="inline-flex items-center gap-1.5 py-2 px-3 md:px-4 border border-gray-200 shadow-sm text-sm font-medium rounded-full text-gray-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              <FiUsers className="w-4 h-4" />
              <span className="hidden sm:inline">Archived Students</span>
              <span className="sm:hidden">Archived</span>
            </button>
            <button
              type="button"
              onClick={() => history.push("/graduated-students")}
              className="inline-flex items-center gap-1.5 py-2 px-3 md:px-4 border border-gray-200 shadow-sm text-sm font-medium rounded-full text-gray-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              <FiAward className="w-4 h-4" />
              <span className="hidden sm:inline">Graduated Students</span>
              <span className="sm:hidden">Graduated</span>
            </button>

            {isGroupingEnabled && (
              <button
                type="button"
                onClick={() => setIsCreateGroupModalOpen(true)}
                className="inline-flex items-center gap-1.5 py-2 px-3 md:px-4 border border-indigo-200 shadow-sm text-sm font-medium rounded-full text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
              >
                <FiLayers className="w-4 h-4" />
                <span className="hidden sm:inline">Create Class Group</span>
                <span className="sm:hidden">+ Group</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => openAddClass("single")}
              className="inline-flex justify-center py-2 px-3 md:px-4 border border-transparent shadow-sm text-sm font-medium rounded-full text-white bg-[#3F3DA5] hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              <span className="hidden sm:inline">Add New Class</span>
              <span className="sm:hidden">+ Class</span>
            </button>
          </div>
        </div>
      </div>

      {/* Discovery prompt: grouping is off but class names look like arms of one level */}
      {!isGroupingEnabled && !groupingPromptDismissed && detectedArmGroups.length > 0 && (
        <div className="mx-4 md:mx-10 mt-4 rounded-2xl border border-indigo-100 bg-indigo-50/70 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white text-indigo-600 rounded-xl flex-shrink-0">
              <FiLayers className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-indigo-950">
                {detectedArmGroups[0].base} looks like it has {detectedArmGroups[0].classes.length} arms
                {detectedArmGroups.length > 1 &&
                  ` (and ${detectedArmGroups.length - 1} other level${detectedArmGroups.length > 2 ? "s" : ""} too)`}
              </p>
              <p className="mt-0.5 text-xs text-indigo-800">
                Group {detectedArmGroups[0].classes.map((c) => c.class_name).join(", ")} so you can
                set a subject, its teacher and its tests once for every arm. Students and fees stay
                as they are.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={dismissGroupingPrompt}
              className="px-3 py-2 text-xs font-medium text-slate-600 hover:bg-white/70 rounded-xl"
            >
              Not now
            </button>
            <button
              type="button"
              onClick={handleEnableGrouping}
              disabled={enablingGrouping}
              className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm disabled:opacity-60"
            >
              {enablingGrouping ? "Turning on..." : "Turn on class grouping"}
            </button>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-2 mx-2 mt-3 md:mt-5 p-2 md:p-5">
        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg rounded-3xl p-4 md:p-8 md:pt-9">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Total Student</p>
              <p className="text-2xl font-semibold">{totalStudents || "0"}</p>
            </div>
            <button
              type="button"
              className="bg-[#3371E8] text-white text-2xl opacity-90 hover:drop-shadow-xl rounded-xl py-4 px-4"
            >
              <img src={Icons.userMultiple} alt="Wallet Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg rounded-3xl p-4 md:p-8 md:pt-9">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Total Subjects</p>
              <p className="text-2xl font-semibold">{totalSubjects || "0"}</p>
            </div>
            <button
              type="button"
              className="bg-[#41D3BD] text-white text-2xl opacity-90 hover:drop-shadow-xl rounded-xl py-4 px-4"
            >
              <img src={Icons.coupon} alt="Payout Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg rounded-3xl p-4 md:p-8 md:pt-9">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">
                {isGroupingEnabled ? `Total ${subclassLabel}s` : "Total Classes"}
              </p>
              <p className="text-2xl font-semibold">{classes?.length || "0"}</p>
            </div>
            <button
              type="button"
              className="bg-[#E26E6A] text-white text-2xl opacity-90 hover:drop-shadow-xl rounded-xl py-4 px-4"
            >
              <img src={Icons.taskDaily} alt="Cashflow Icon" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="m-2 md:m-10 mt-3 md:mt-6 p-2 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
        {/* View mode toggle when grouping is enabled */}
        {isGroupingEnabled && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setViewMode("all")}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  viewMode === "all"
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <FiList className="w-4 h-4" />
                All {subclassLabel}s ({classes?.length || 0})
              </button>
              <button
                type="button"
                onClick={() => setViewMode("groups")}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  viewMode === "groups"
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <FiGrid className="w-4 h-4" />
                Class Groups ({classGroups.length})
              </button>
            </div>

            {/* Batch Selection Action Bar */}
            {selectedRows.length > 0 && (
              <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 px-3 py-1.5 rounded-xl">
                <span className="text-xs font-semibold text-indigo-800">
                  {selectedRows.length} {subclassLabel.toLowerCase()}
                  {selectedRows.length !== 1 ? "s" : ""} selected
                </span>
                <button
                  type="button"
                  onClick={handleBatchGroupSelected}
                  className="px-3 py-1 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors"
                >
                  Group as...
                </button>
              </div>
            )}
          </div>
        )}

        {isGroupingEnabled && viewMode === "groups" && (
          <div className="mb-6 rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-white p-5">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h3 className="text-sm font-bold text-slate-900">What a class group does</h3>
                <p className="mt-1 text-xs text-slate-600">
                  Group JSS 1A, JSS 1B and JSS 1C under "JSS 1" to set a subject, its teacher and
                  its tests once for every {subclassLabel.toLowerCase()}. Each{" "}
                  {subclassLabel.toLowerCase()} keeps its own students and class teacher.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <span className="rounded-lg bg-white border border-indigo-100 px-3 py-2 text-slate-700">
                  <strong className="text-indigo-700">1.</strong> Create the group
                </span>
                <span className="rounded-lg bg-white border border-indigo-100 px-3 py-2 text-slate-700">
                  <strong className="text-indigo-700">2.</strong> Choose its {armsPlural}
                </span>
                <span className="rounded-lg bg-white border border-indigo-100 px-3 py-2 text-slate-700">
                  <strong className="text-indigo-700">3.</strong> Choose shared subjects
                </span>
              </div>
            </div>
          </div>
        )}

        {/* View Mode: Grouped Cards */}
        {isGroupingEnabled && viewMode === "groups" ? (
          <div className="space-y-8">
            {loadingGroups ? (
              <SkeletonTable rows={3} columns={3} />
            ) : classGroups.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-3xl p-6">
                <FiLayers className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <h3 className="text-base font-bold text-slate-800">No class groups yet</h3>
                <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                  {detectedArmGroups.length > 0
                    ? `We found ${detectedArmGroups.length} level${detectedArmGroups.length !== 1 ? "s" : ""} that look like they have ${armsPlural} (e.g. ${detectedArmGroups[0].base}). Create a group to get started — we'll suggest them for you.`
                    : `Create a group for a class level (e.g. "Primary 1" or "JSS 1"), then choose its ${armsPlural}.`}
                </p>
                <button
                  type="button"
                  onClick={() => setIsCreateGroupModalOpen(true)}
                  className="mt-4 px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm transition-colors"
                >
                  + Create your first class group
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {classGroups.map((group) => {
                  const members = (classes || []).filter(
                    (c) => classGroupApi.getClassGroupId(c.class_group_id) === group._id,
                  );
                  const groupStudents = members.reduce((sum, m) => sum + (m.studentCount || 0), 0);
                  const armsWithoutNextClass = members.filter(
                    (m) => !m.next_class && !m.is_last_class,
                  ).length;
                  const setupHints = [
                    members.length === 0 && `Choose its ${armsPlural}`,
                    members.length > 0 && !group.default_class_price && "Set default fees",
                    armsWithoutNextClass > 0 &&
                      `${armsWithoutNextClass} ${pluralize(subclassLabel, armsWithoutNextClass).toLowerCase()} need a next class for promotion`,
                  ].filter(Boolean);

                  return (
                    <div
                      key={group._id}
                      className="p-6 rounded-3xl border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md transition-all flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
                              <FiLayers className="w-6 h-6" />
                            </div>
                            <div>
                              <h4 className="text-lg font-bold text-slate-900">{group.name}</h4>
                              <p className="text-xs text-slate-500">
                                {members.length} {pluralize(subclassLabel, members.length).toLowerCase()} • {groupStudents} students
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Members arms chips */}
                        <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                            {armsPlural}
                          </span>
                          {members.length === 0 ? (
                            <p className="text-xs text-slate-400">None yet</p>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {members.map((m) => (
                                <span
                                  key={m._id}
                                  className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-800 border border-slate-200"
                                  title={m.class_name}
                                >
                                  {m.group_label || m.class_name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {setupHints.length > 0 && (
                          <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 px-3 py-2.5">
                            <p className="text-[11px] font-semibold text-amber-800">To finish setting up:</p>
                            <ul className="mt-1 space-y-0.5 text-xs text-amber-800 list-disc list-inside">
                              {setupHints.map((hint) => (
                                <li key={hint}>{hint}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => history.push(`/class-groups/${group._id}`)}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-800"
                        >
                          {setupHints.length > 0 ? "Continue setup" : "Open group"}{" "}
                          <FiArrowRight className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setClassesToLink([]);
                            setJustCreatedGroup(null);
                            setTargetGroupIdForLink(group._id);
                            setIsLinkModalOpen(true);
                          }}
                          className="text-xs text-slate-500 hover:text-slate-700 font-medium"
                        >
                          {members.length === 0
                            ? `Choose ${armsPlural}`
                            : `+ Add ${subclassLabel.toLowerCase()}`}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Ungrouped classes section */}
            {classes.some((c) => !c.class_group_id) && (
              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-slate-900">
                    Classes not in a group ({classes.filter((c) => !c.class_group_id).length})
                  </h4>
                  <p className="text-xs text-slate-500">
                    That's fine for levels with only one class. Add a class to a group if it's an{" "}
                    {subclassLabel.toLowerCase()} of a level.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // Start with nothing ticked: pre-ticking every ungrouped class made it too
                    // easy to put unrelated levels into one group.
                    setClassesToLink([]);
                    setJustCreatedGroup(null);
                    setTargetGroupIdForLink("");
                    setIsLinkModalOpen(true);
                  }}
                  className="px-4 py-2 text-xs font-semibold text-indigo-700 bg-indigo-100 hover:bg-indigo-200 rounded-xl transition-colors flex-shrink-0"
                >
                  Add to a class group
                </button>
              </div>
            )}
          </div>
        ) : (
          /* View Mode: Flat DataTable (Default & Ungrouped Mode) */
          <div className="border rounded-3xl">
            <DataTable
              data={classes}
              columns={columns}
              getRowId={(row) => row._id}
              pagination
              paginationComponent={CustomPagination}
              paginationRowsPerPageOptions={[10, 20, 30, 50]}
              customStyles={customStyles}
              progressPending={loading}
              progressComponent={<SkeletonTable rows={5} columns={6} />}
              highlightOnHover
              striped
              selectableRows={isGroupingEnabled}
              onSelectedRowsChange={({ selectedRows: rows }) => setSelectedRows(rows)}
            />
          </div>
        )}

        {/* Existing Add Class Modal */}
        {isModalOpen && (
          <AddClassModal
            closeModal={toggleModal}
            class_name={class_name}
            setClassname={setClassname}
            price1={price1}
            setPrice1={setPrice1}
            price2={price2}
            setPrice2={setPrice2}
            price3={price3}
            setPrice3={setPrice3}
            level_of_education={level_of_education}
            setLevelOfEducation={setLevelOfEducation}
            next_class={next_class}
            setNextClass={setNextClass}
            is_last_class={is_last_class}
            setIsLastClass={setIsLastClass}
            classes={classes}
            error={error}
            success={success}
            loading={loading}
            submitHandler={submitHandler}
            subclassLabel={subclassLabel}
            initialMode={addClassMode}
            onGroupCreated={(newGroupId) => {
              refreshClassGroups();
              fetchClasses();
              setViewMode("groups");
              // Arms, fees and next class are set by the wizard; the group page is where
              // the admin continues with shared subjects and teachers.
              if (newGroupId) {
                groupRedirectTimer.current = setTimeout(
                  () => history.push(`/class-groups/${newGroupId}`),
                  1500,
                );
              }
            }}
          />
        )}

        {/* Create Class Group Modal */}
        {isCreateGroupModalOpen && (
          <CreateClassGroupModal
            isOpen={isCreateGroupModalOpen}
            onClose={() => setIsCreateGroupModalOpen(false)}
            schoolId={schoolID}
            subclassLabel={subclassLabel}
            suggestions={detectedArmGroups}
            classGroups={classGroups}
            // Creating the group is step one; continue straight to choosing its arms so
            // the admin never has to discover the follow-up action on their own.
            onSuccess={handleGroupCreated}
            onChooseCreateNew={() => openAddClass("grouped")}
          />
        )}

        {/* Link Subclasses Modal */}
        {isLinkModalOpen && (
          <LinkSubclassesModal
            isOpen={isLinkModalOpen}
            onClose={() => {
              setIsLinkModalOpen(false);
              setClassesToLink([]);
              setSelectedRows([]);
              setTargetGroupIdForLink("");
              setJustCreatedGroup(null);
            }}
            schoolId={schoolID}
            classGroups={classGroups}
            availableClasses={linkModalAvailableClasses}
            initialSelectedClasses={classesToLink}
            targetClassGroupId={targetGroupIdForLink}
            targetGroupName={justCreatedGroup?.name || ""}
            lockTargetGroup={Boolean(justCreatedGroup)}
            subclassLabel={subclassLabel}
            onSuccess={() => {
              fetchClasses();
              refreshClassGroups();
              setViewMode("groups");
            }}
          />
        )}

        {/* Unlink Subclass Confirmation Modal */}
        {unlinkingClass && (
          <ConfirmationModal
            isVisible={!!unlinkingClass}
            title="Remove from group"
            description={`Remove "${unlinkingClass.class_name}" from its class group? It becomes a standalone class again. Its students and fees stay the same, but it will no longer share subjects, subject teachers or tests with the other ${armsPlural}.`}
            deleting={unlinkingLoading}
            onConfirm={handleConfirmUnlink}
            onCancel={() => setUnlinkingClass(null)}
          />
        )}

        {/* Delete Class Confirmation Modal */}
        {deletingClass && (
          <ConfirmationModal
            isVisible={!!deletingClass}
            title="Delete Class"
            description={
              `Are you sure you want to delete "${deletingClass.class_name}"? This will permanently ` +
              `delete this class${deletingClass.studentCount > 0 ? ` and all ${deletingClass.studentCount} student${deletingClass.studentCount !== 1 ? "s" : ""} enrolled in it` : ""}. ` +
              `This action cannot be undone.`
            }
            deleting={deletingClassLoading}
            onConfirm={handleConfirmDeleteClass}
            onCancel={() => setDeletingClass(null)}
          />
        )}

        {/* Student Promotion Modal */}
        {isPromotionModalOpen && selectedClass && (
          <StudentPromotionModal
            isOpen={isPromotionModalOpen}
            onClose={() => togglePromotionModal()}
            students={students}
            classInfo={selectedClass}
            schoolCode={schoolCode}
            onPromotionSuccess={handlePromotionSuccess}
          />
        )}

        {/* Skip Promotion / Demotion Modal */}
        {isTransferModalOpen && selectedClassForTransfer && (
          <ClassTransferModal
            isOpen={isTransferModalOpen}
            onClose={() => toggleTransferModal()}
            students={students}
            classInfo={selectedClassForTransfer}
            allClasses={classes}
            schoolCode={schoolCode}
            onTransferSuccess={handlePromotionSuccess}
          />
        )}

        {/* Class Detail Modal */}
        {isClassDetailModalOpen && selectedClassForDetail && (
          <ClassDetailModal
            isOpen={isClassDetailModalOpen}
            onClose={() => toggleClassDetailModal()}
            classData={selectedClassForDetail}
            schoolId={schoolID}
          />
        )}
      </div>
    </>
  );
};

export default Classes;
